// ---- a thing that stands in the field ----
// The parts of reading a model that have nothing to do with what the model is:
// which objects are skin, which way is up, and how far a model's feet reach
// from where it stands. An animal needs them, Jiddo needs them, and a bowl of
// tabbouleh needs the last one to know how much room it takes on the grass.
//
// They came out of jiddo's old rig-reading code when the engine was split
// out; that code has gone since, and these are what every model needs of it.
import * as THREE from 'three';
import type {Body,Collider} from './types';

export const UP=new THREE.Vector3(0,1,0);
export const isSkinned=(o:THREE.Object3D):o is THREE.SkinnedMesh=>(o as THREE.SkinnedMesh).isSkinnedMesh===true;
export const isMesh=(o:THREE.Object3D):o is THREE.Mesh=>(o as THREE.Mesh).isMesh===true;
// A fresh model, or a fresh clone of one, has no bone matrices until this, and
// without them its box is the unskinned one — a fraction of the real size,
// which once scaled every animal up 2.4× to fill it. Before anything measures it.
export function posed(m:THREE.Object3D){m.updateMatrixWorld(true);m.traverse(o=>{if(isSkinned(o))o.skeleton.update();});}
// Scaled to stand h tall with its feet on the grass, measured off its posed
// box; what comes back is its size once scaled.
export function fitHeight(m:THREE.Object3D,h:number){
  const box=new THREE.Box3().setFromObject(m), size=box.getSize(new THREE.Vector3()), s=h/size.y;
  m.scale.setScalar(s); m.position.y=-box.min.y*s;
  return size.multiplyScalar(s);
}
// A collision sphere hung on a body at a fixed offset (separate(), field.ts):
// three down an animal's length, one under a plate.
export function addCollider(b:Body,x:number,y:number,z:number,r:number){
  const c=new THREE.Object3D() as Collider; c.position.set(x,y,z); c.r=r; c.w=new THREE.Vector3(); b.add(c); b.cols.push(c);
}
// How far a model's feet reach from where it stands: the farthest skin at floor
// level from its group's origin, along the ground. Feet come in mirrored pairs,
// so none is ever on the midline; what hangs to the ground there is a trunk or
// a tail — the elephant's trunk tip rests a hair off the floor between its
// front feet, and it is not a foot — so skin near the midline is not counted.
// Read once the model is turned, scaled and set on the grass, so it comes out
// in the field's units.
//
// A dish has no feet, and that is fine: every vertex of a bowl is near the
// floor, so what comes back is simply how far it spreads — which is what the
// collisions and the pond want to know about it anyway.
const FV=new THREE.Vector3();
export function footprint(g:THREE.Object3D){
  posed(g);
  const meshes:THREE.Mesh[]=[]; g.traverse(o=>{if(isMesh(o))meshes.push(o);});
  const each=(f:(v:THREE.Vector3)=>void)=>{for(const o of meshes){const pos=o.geometry.attributes.position;
    for(let i=0;i<pos.count;i++){o.getVertexPosition(i,FV);o.localToWorld(FV);g.worldToLocal(FV);f(FV);}}};
  let floor=Infinity, top=-Infinity, half=0, reach=0;
  each(v=>{floor=Math.min(floor,v.y);top=Math.max(top,v.y);half=Math.max(half,Math.abs(v.x));});
  each(v=>{if(v.y<floor+0.06*(top-floor)&&Math.abs(v.x)>0.3*half)reach=Math.max(reach,Math.hypot(v.x,v.z));});
  return reach;
}
